
import React, { useState, useEffect } from "react";
import axios from "axios";
import { BASE } from "../utils/api";
import { isWithinLast24h, last24hCutoff } from "../utils/timeWindow";

// Game name resolver
const resolveGameName = (gameTitle?: string): string => {
  if (!gameTitle) return 'Casino Game';
  if (/^[a-f0-9]{20,}$/i.test(gameTitle)) return 'Casino Game';
  return gameTitle;
};

interface Transaction {
  id: string;
  transaction_id: string;
  round_id?: string;
  user_name: string;
  user_id: string;
  game_title?: string;
  transaction_type: string;
  amount: number;
  currency_code: string;
  transaction_timestamp: string;
  transaction_status?: string;
}

interface MergedRow {
  id: string;
  user_name: string;
  user_id: string;
  game_title: string;
  currency_code: string;
  betAmount: number | null;
  resultAmount: number | null;
  resultType: string;
  transaction_timestamp: string;
}

interface PaginationState {
  totalCount: number;
  totalPages: number;
  currentPage: number;
  limit: number;
}

interface TransactionResponse {
  transactions: Transaction[];
  pagination: PaginationState;
}

const History: React.FC = () => {
  const staffId = localStorage.getItem('currentUserId');
  const [allTransactions, setAllTransactions] = useState<Transaction[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [userId, setUserId] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  // Default view = last 24 hours only. Switched off as soon as a date filter is used.
  const [last24h, setLast24h] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const pageSize = 10;

  const pickDateFrom = (v: string) => {
    setDateFrom(v);
    if (v) setLast24h(false);
    setCurrentPage(1);
  };

  const pickDateTo = (v: string) => {
    setDateTo(v);
    if (v) setLast24h(false);
    setCurrentPage(1);
  };

  const toggle24h = (on: boolean) => {
    setLast24h(on);
    if (on) { setDateFrom(''); setDateTo(''); }
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchAllData();
  }, [searchTerm, userId]);

  const fetchAllData = async (): Promise<void> => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers: Record<string, string> = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(staffId ? { 'x-staff-id': staffId } : {})
      };

      // Fetch all data, let client handle filtering and pagination
      const params = new URLSearchParams();
      params.set('page', '1');
      params.set('limit', '500');
      if (searchTerm) params.set('search', searchTerm);
      if (userId) params.set('userId', userId);

      const url = `${BASE}/betHistory/transactions?${params.toString()}`;
      const { data } = await axios.get<TransactionResponse>(url, { headers });
      setAllTransactions(data.transactions);
    } catch (err) {
      console.error("Failed to fetch transaction data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Merge BET + WIN/LOSS by round_id
  const getMergedRows = (): MergedRow[] => {
    const roundGroups: Record<string, Transaction[]> = {};
    const noRound: Transaction[] = [];

    allTransactions.forEach(tx => {
      if (tx.round_id) {
        if (!roundGroups[tx.round_id]) roundGroups[tx.round_id] = [];
        roundGroups[tx.round_id].push(tx);
      } else {
        noRound.push(tx);
      }
    });

    const merged: MergedRow[] = [];

    Object.values(roundGroups).forEach(group => {
      const betTx = group.find(t => t.transaction_type === 'BET');
      const winTx = group.find(t => t.transaction_type === 'WIN');
      const lossTx = group.find(t => t.transaction_type === 'LOSS');
      const resultTx = winTx || lossTx;
      const baseTx = betTx || resultTx || group[0];

      if (betTx && resultTx) {
        merged.push({
          id: baseTx.id,
          user_name: baseTx.user_name,
          user_id: baseTx.user_id,
          game_title: resolveGameName(baseTx.game_title),
          currency_code: baseTx.currency_code,
          betAmount: betTx.amount,
          resultAmount: resultTx.amount,
          resultType: resultTx.transaction_type,
          transaction_timestamp: betTx.transaction_timestamp,
        });
      } else if (betTx) {
        merged.push({
          id: baseTx.id,
          user_name: baseTx.user_name,
          user_id: baseTx.user_id,
          game_title: resolveGameName(baseTx.game_title),
          currency_code: baseTx.currency_code,
          betAmount: betTx.amount,
          resultAmount: null,
          resultType: 'LOSS',
          transaction_timestamp: betTx.transaction_timestamp,
        });
      } else if (resultTx) {
        merged.push({
          id: baseTx.id,
          user_name: baseTx.user_name,
          user_id: baseTx.user_id,
          game_title: resolveGameName(baseTx.game_title),
          currency_code: baseTx.currency_code,
          betAmount: null,
          resultAmount: resultTx.amount,
          resultType: resultTx.transaction_type,
          transaction_timestamp: resultTx.transaction_timestamp,
        });
      }
    });

    // Transactions without round_id
    noRound.forEach(tx => {
      merged.push({
        id: tx.id,
        user_name: tx.user_name,
        user_id: tx.user_id,
        game_title: resolveGameName(tx.game_title),
        currency_code: tx.currency_code,
        betAmount: tx.transaction_type === 'BET' ? tx.amount : null,
        resultAmount: tx.transaction_type !== 'BET' ? tx.amount : null,
        resultType: tx.transaction_type,
        transaction_timestamp: tx.transaction_timestamp,
      });
    });

    // Sort descending
    merged.sort((a, b) => new Date(b.transaction_timestamp).getTime() - new Date(a.transaction_timestamp).getTime());
    return merged;
  };

  // Apply client-side filters
  const getFilteredRows = (): MergedRow[] => {
    let rows = getMergedRows();

    // Exclude LuckySport bets (they show in Sports Bet section)
    rows = rows.filter(r => r.game_title !== '大厅' && r.game_title !== 'LuckySport');

    // Default 24 hour window – skipped once an explicit date filter is applied
    if (last24h) {
      const cutoff = last24hCutoff();
      rows = rows.filter(r => isWithinLast24h(r.transaction_timestamp, cutoff));
    }

    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      rows = rows.filter(r =>
        r.user_name?.toLowerCase().includes(s) ||
        r.game_title?.toLowerCase().includes(s) ||
        r.user_id?.toString().includes(s)
      );
    }

    if (userId) {
      rows = rows.filter(r => r.user_id?.toString() === userId);
    }

    if (dateFrom) {
      const from = new Date(dateFrom);
      from.setHours(0, 0, 0, 0);
      rows = rows.filter(r => new Date(r.transaction_timestamp) >= from);
    }

    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      rows = rows.filter(r => new Date(r.transaction_timestamp) <= to);
    }

    return rows;
  };

  const filteredRows = getFilteredRows();
  const totalPages = Math.ceil(filteredRows.length / pageSize);
  const pagedRows = filteredRows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleClearFilters = () => {
    setSearchTerm('');
    setUserId('');
    setDateFrom('');
    setDateTo('');
    setLast24h(true);
    setCurrentPage(1);
  };

  const formatDate = (timestamp: string): string => {
    return new Date(timestamp).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  };

  if (loading && allTransactions.length === 0) {
    return (
      <div className="ml-0 lg:ml-[256px] p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading transaction data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="ml-0 lg:ml-[256px] p-6">
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h1 className="text-2xl font-bold mb-6">Bet History</h1>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              placeholder="Search by name, game..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
            <input
              type="text"
              placeholder="Filter by User ID"
              value={userId}
              onChange={(e) => { setUserId(e.target.value); setCurrentPage(1); }}
              className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => pickDateFrom(e.target.value)}
              className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => pickDateTo(e.target.value)}
              className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Info bar */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={last24h}
                onChange={(e) => toggle24h(e.target.checked)}
              />
              Last 24 hours only
            </label>
            <span className="text-gray-600">
              Showing {pagedRows.length} of {filteredRows.length} rounds
              {last24h ? ' (last 24 hours)' : ''}
            </span>
          </div>
          {(searchTerm || userId || dateFrom || dateTo || !last24h) && (
            <button
              onClick={handleClearFilters}
              className="text-blue-500 hover:text-blue-700 text-sm"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Table */}
        {pagedRows.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-gray-500">
              {last24h ? 'No transactions in the last 24 hours' : 'No transactions found'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full bg-white shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-100 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Game</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Coin</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Bet</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Result</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pagedRows.map((row, idx) => (
                  <tr key={row.id || idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">{row.user_name || '-'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{row.game_title}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{row.currency_code || '-'}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {row.betAmount != null ? row.betAmount.toFixed(2) : '-'}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <span className={row.resultType === 'WIN' ? 'text-green-600' : row.resultType === 'LOSS' ? 'text-red-600' : ''}>
                        {row.resultType === 'WIN' && row.resultAmount != null
                          ? `+${row.resultAmount.toFixed(2)}`
                          : row.resultType === 'LOSS'
                            ? `-${(row.betAmount || 0).toFixed(2)}`
                            : row.resultAmount != null ? row.resultAmount.toFixed(2) : '-'
                        }
                      </span>
                      <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                        row.resultType === 'WIN' ? 'bg-green-100 text-green-700' : 
                        row.resultType === 'LOSS' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {row.resultType}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{formatDate(row.transaction_timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center mt-6">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 mr-4 bg-gray-100 rounded border hover:bg-gray-200 disabled:opacity-50"
            >
              Previous
            </button>
            <div className="text-center px-4 py-2 bg-gray-50 rounded border min-w-[80px]">
              {currentPage} / {totalPages}
            </div>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="px-4 py-2 ml-4 bg-gray-100 rounded border hover:bg-gray-200 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default History;
